// src/components/Sidebar/SidebarQuickAdd.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { FiEdit2, FiPlus, FiX } from "react-icons/fi";
import axiosInstace from "../../axiosInstace";
import { useAuth } from "../../contexts/AuthContext";
import { normalizeRole, ROLES } from "../../config/roles";
import "./SidebarQuickAdd.scss";

/**
 * Quick Add
 * - Sidebar button + popup (classname prefix: quickadd-classname__*)
 * - Tab "Task": add tasks, each with its own checklist items, in one go
 * - Tab "Checklist": add checklist items to a task that already exists
 */

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const projectIdOf = (p) => p?.projectId ?? p?.id;
const projectNameOf = (p) =>
  p?.projectTitle ?? p?.title ?? p?.name ?? "Untitled";
const stageIdOf = (s) => s?.taskId ?? s?.id;
const stageNameOf = (s) => s?.taskTitle ?? s?.title ?? "Untitled stage";
const todoIdOf = (t) => t?.toDoId ?? t?.id;
const todoNameOf = (t) => t?.toDoTitle ?? t?.title ?? "Untitled task";

// ASSUMPTION: confirm these match the priority values your backend accepts.
const PRIORITIES = ["LOW", "MEDIUM", "HIGH"];
const priorityLabel = (p) => p.charAt(0) + p.slice(1).toLowerCase();

const normName = (s) =>
  String(s ?? "")
    .trim()
    .toLowerCase();

const splitLines = (text) =>
  String(text)
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

// ASSUMPTION: confirm these two body shapes against the Swagger doc.
// A checklist item inside POST /api/emp/todos -> listRequestDTOS[]
const toListRequest = (text) => ({ toDoListTitle: text });
// POST /api/emp/todolists/create (checklist tab)
const toChecklistCreateBody = (todoId, text) => ({
  toDoId: Number(todoId),
  toDoListTitle: text,
});

// Initial stage position from /project/:id/stages (same shapes the
// dashboard handles). Falls back to the first position, then 1.
function pickInitialStagePositionId(raw) {
  const list = Array.isArray(raw) ? raw : [];
  const stages = list.some((r) => Array.isArray(r?.stageResponseDTOList))
    ? list.flatMap((r) => r.stageResponseDTOList || [])
    : list;
  const positions = stages.flatMap(
    (s) => s?.stagePositionResponseDTOList || [],
  );
  const chosen = positions.find((p) => p?.initial) ?? positions[0];
  return chosen?.stagePositionId ?? chosen?.id ?? 1;
}

// tone: "low" | "medium" | "high" -> green / yellow / red priority pills
// avatar: name to show as a small initial bubble (assignee pills)
function Pill({ active, onClick, children, title, disabled, tone, avatar }) {
  return (
    <button
      type="button"
      className={[
        "quickadd-classname__pill",
        tone ? `quickadd-classname__pill--${tone}` : "",
        active ? "active" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-pressed={!!active}
      title={title}
      disabled={disabled}
      onClick={onClick}
    >
      {avatar != null && (
        <span className="quickadd-classname__pill-avatar" aria-hidden="true">
          {String(avatar || "?")
            .charAt(0)
            .toUpperCase()}
        </span>
      )}
      <span className="quickadd-classname__pill-text">{children}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Popup                                                               */
/* ------------------------------------------------------------------ */

function QuickAddModal({ projects, defaultProjectId, onClose }) {
  const { user } = useAuth();
  const currentEmpId = (() => {
    if (user?.empId != null) return user.empId;
    try {
      return (
        JSON.parse(sessionStorage.getItem("user") || "null")?.empId ?? null
      );
    } catch {
      return null;
    }
  })();

  const [tab, setTab] = useState("task"); // "task" | "checklist"
  const [projectId, setProjectId] = useState(defaultProjectId ?? null);
  const [stageId, setStageId] = useState(null);
  const [todoId, setTodoId] = useState(null);

  const [stages, setStages] = useState([]);
  const [members, setMembers] = useState([]);
  const [loadingProject, setLoadingProject] = useState(false);

  // Defaults applied to each newly added task (editable per task afterwards)
  const [defaultPriority, setDefaultPriority] = useState("MEDIUM");
  const [defaultAssignee, setDefaultAssignee] = useState(currentEmpId);
  const [projectError, setProjectError] = useState("");

  // Task tab: [{ id, title, items: [{ id, text }] }]
  const [tasks, setTasks] = useState([]);
  const [taskDraft, setTaskDraft] = useState("");
  const [itemDrafts, setItemDrafts] = useState({}); // { [taskLocalId]: string }
  const [editingTaskId, setEditingTaskId] = useState(null); // task whose name is being edited

  // Checklist tab: [{ id, text }]
  const [queue, setQueue] = useState([]);
  const [queueDraft, setQueueDraft] = useState("");

  const [saving, setSaving] = useState(false);
  // Results are shown as toasts (ToastContainer already lives in App).
  // setMessage(null) calls elsewhere are intentionally no-ops.
  const setMessage = (m) => {
    if (!m) return;
    if (m.type === "error") toast.error(m.text);
    else toast.success(m.text);
  };

  const taskInputRef = useRef(null);
  const queueInputRef = useRef(null);
  const itemInputRefs = useRef({});
  const focusItemFor = useRef(null);
  const idRef = useRef(1);
  const nextId = () => idRef.current++;

  /* ---------- body scroll lock + Esc ---------- */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  /* ---------- load the chosen project ---------- */
  const loadProject = useCallback(async (id) => {
    if (!id) {
      setStages([]);
      setMembers([]);
      return;
    }
    setLoadingProject(true);
    setProjectError("");
    try {
      const [res, membersRes] = await Promise.all([
        axiosInstace.get(`/api/emp/project/${id}/full`),
        axiosInstace
          .get(`/api/emp/projects/projectmemberaccess/${id}/allmembers`)
          .catch(() => null),
      ]);
      const data = res?.data?.data ?? null;
      setStages(Array.isArray(data?.tasks) ? data.tasks : []);
      setMembers(
        Array.isArray(membersRes?.data?.data) ? membersRes.data.data : [],
      );
    } catch (err) {
      setStages([]);
      setMembers([]);
      setProjectError(
        err?.response?.data?.message || "Couldn't load this project.",
      );
    } finally {
      setLoadingProject(false);
    }
  }, []);

  useEffect(() => {
    setStageId(null);
    setTodoId(null);
    loadProject(projectId);
  }, [projectId, loadProject]);

  useEffect(() => {
    if (!stageId && stages.length === 1) setStageId(stageIdOf(stages[0]));
  }, [stages, stageId]);

  const selectedStage = useMemo(
    () => stages.find((s) => String(stageIdOf(s)) === String(stageId)) || null,
    [stages, stageId],
  );
  const todos = useMemo(
    () => selectedStage?.todoRequestDTOList || [],
    [selectedStage],
  );

  // Same rule as the dashboard: only Creator / Approver can assign someone
  // else; everyone else's tasks go to themselves.
  const myRole = normalizeRole(
    members.find((m) => String(m.empId) === String(currentEmpId))?.roleType,
  );
  const canAssign = myRole === ROLES.CREATOR || myRole === ROLES.APPROVER;
  const assigneeFor = (id) => (canAssign && id != null ? id : currentEmpId);

  // New project => queued tasks belonged to the old one; assignees may not exist here.
  useEffect(() => {
    setTasks([]);
    setItemDrafts({});
    setDefaultAssignee(currentEmpId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (tab === "checklist" && !todoId && todos.length === 1)
      setTodoId(todoIdOf(todos[0]));
  }, [tab, todos, todoId]);

  const ready =
    !!projectId &&
    !!stageId &&
    (tab === "task" || (tab === "checklist" && !!todoId));

  /* ---------- focus management ---------- */
  useEffect(() => {
    if (!ready) return;
    (tab === "task" ? taskInputRef : queueInputRef).current?.focus();
  }, [ready, tab]);

  // After adding a task, jump straight into its checklist input.
  useEffect(() => {
    const id = focusItemFor.current;
    if (id != null && itemInputRefs.current[id]) {
      itemInputRefs.current[id].focus();
      focusItemFor.current = null;
    }
  }, [tasks]);

  const switchTab = (t) => {
    setTab(t);
    setMessage(null);
  };

  /* ---------- Task tab handlers ---------- */
  const addTasks = (raw) => {
    const titles = splitLines(raw);
    if (!titles.length) return;
    const created = titles.map((title) => ({
      id: nextId(),
      title,
      items: [],
      remark: null, // null = hidden, string = shown (can be cleared)
      priority: defaultPriority,
      assigneeId: assigneeFor(defaultAssignee),
    }));
    setTasks((t) => [...t, ...created]);
    setTaskDraft("");
    setMessage(null);
    // Focus stays in the task box so you can keep adding tasks with Enter.
  };

  const addItems = (taskLocalId, raw) => {
    const texts = splitLines(raw);
    if (!texts.length) return;
    setTasks((all) =>
      all.map((t) =>
        t.id === taskLocalId
          ? {
              ...t,
              items: [
                ...t.items,
                ...texts.map((text) => ({ id: nextId(), text })),
              ],
            }
          : t,
      ),
    );
    setItemDrafts((d) => ({ ...d, [taskLocalId]: "" }));
    setMessage(null);
  };

  const patchTask = (taskLocalId, patch) =>
    setTasks((all) =>
      all.map((t) => (t.id === taskLocalId ? { ...t, ...patch } : t)),
    );

  const commitTaskTitle = (taskLocalId, value) => {
    const title = String(value ?? "").trim();
    if (title) patchTask(taskLocalId, { title });
    setEditingTaskId(null);
  };

  const removeItem = (taskLocalId, itemId) =>
    setTasks((all) =>
      all.map((t) =>
        t.id === taskLocalId
          ? { ...t, items: t.items.filter((i) => i.id !== itemId) }
          : t,
      ),
    );

  const onTaskKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTasks(taskDraft);
    }
  };
  const onTaskPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (/\r?\n/.test(text.trim())) {
      e.preventDefault();
      addTasks(taskDraft ? `${taskDraft}\n${text}` : text);
    }
  };

  const onItemKeyDown = (e, taskLocalId) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation(); // don't close the popup
      taskInputRef.current?.focus();
      return;
    }
    if (e.key !== "Enter") return;
    e.preventDefault();
    const draft = itemDrafts[taskLocalId] || "";
    if (draft.trim()) addItems(taskLocalId, draft);
    else taskInputRef.current?.focus(); // empty Enter -> back to a new task
  };
  const onItemPaste = (e, taskLocalId) => {
    const text = e.clipboardData?.getData("text") || "";
    if (/\r?\n/.test(text.trim())) {
      e.preventDefault();
      const draft = itemDrafts[taskLocalId] || "";
      addItems(taskLocalId, draft ? `${draft}\n${text}` : text);
    }
  };

  /* ---------- Checklist tab handlers ---------- */
  const addQueue = (raw) => {
    const texts = splitLines(raw);
    if (!texts.length) return;
    setQueue((q) => [...q, ...texts.map((text) => ({ id: nextId(), text }))]);
    setQueueDraft("");
    setMessage(null);
  };
  const onQueueKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addQueue(queueDraft);
    } else if (e.key === "Backspace" && !queueDraft && queue.length) {
      setQueue((q) => q.slice(0, -1));
    }
  };
  const onQueuePaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (/\r?\n/.test(text.trim())) {
      e.preventDefault();
      addQueue(queueDraft ? `${queueDraft}\n${text}` : text);
    }
  };

  /* ---------- What would be saved (includes text still in the boxes) ---------- */
  const pendingTasks = useMemo(() => {
    const withDrafts = tasks.map((t) => ({
      ...t,
      items: [
        ...t.items,
        ...splitLines(itemDrafts[t.id] || "").map((text) => ({ id: -1, text })),
      ],
    }));
    const extra = splitLines(taskDraft).map((title) => ({
      id: -1,
      title,
      items: [],
      remark: null,
      priority: defaultPriority,
      assigneeId: assigneeFor(defaultAssignee),
    }));
    return [...withDrafts, ...extra];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tasks,
    itemDrafts,
    taskDraft,
    defaultPriority,
    defaultAssignee,
    canAssign,
  ]);

  const pendingChecklist = useMemo(
    () => [
      ...queue,
      ...splitLines(queueDraft).map((text) => ({ id: -1, text })),
    ],
    [queue, queueDraft],
  );

  // A stage can't have two tasks with the same name: check against tasks
  // already in the stage AND against the other tasks queued here.
  const duplicateNames = useMemo(() => {
    const existing = new Set(todos.map((t) => normName(todoNameOf(t))));
    const counts = {};
    pendingTasks.forEach((t) => {
      const k = normName(t.title);
      counts[k] = (counts[k] || 0) + 1;
    });
    const dups = new Set();
    Object.entries(counts).forEach(([k, n]) => {
      if (n > 1 || existing.has(k)) dups.add(k);
    });
    return dups;
  }, [pendingTasks, todos]);
  const isDuplicate = (title) => duplicateNames.has(normName(title));
  const hasDuplicate = tab === "task" && duplicateNames.size > 0;

  const taskItemCount = pendingTasks.reduce((n, t) => n + t.items.length, 0);
  const saveCount =
    tab === "task" ? pendingTasks.length : pendingChecklist.length;

  const saveLabel = (() => {
    if (saving) return "Saving…";
    if (saveCount === 0) return "Save";
    if (tab === "task") {
      const t = `${saveCount} task${saveCount === 1 ? "" : "s"}`;
      return taskItemCount
        ? `Save ${t} + ${taskItemCount} item${taskItemCount === 1 ? "" : "s"}`
        : `Save ${t}`;
    }
    return `Save ${saveCount} item${saveCount === 1 ? "" : "s"}`;
  })();

  /* ---------- Save ---------- */
  const afterSave = (savedCount, failedCount, firstError, noun) => {
    setMessage(
      failedCount === 0
        ? {
            type: "success",
            text: `${savedCount} ${noun}${savedCount === 1 ? "" : "s"} added. Keep going or close.`,
          }
        : {
            type: "error",
            text: `${savedCount} saved, ${failedCount} failed: ${firstError}`,
          },
    );
    if (savedCount > 0) {
      loadProject(projectId);
      try {
        window.dispatchEvent(
          new CustomEvent("project-updated", {
            detail: { action: "quick-add", projectId },
          }),
        );
      } catch {
        /* ignore */
      }
    }
  };

  const saveTasks = async () => {
    const stagesRes = await axiosInstace
      .get(`/api/emp/project/${projectId}/stages`)
      .catch(() => null);
    const stagePositionId = pickInitialStagePositionId(stagesRes?.data?.data);
    // accessId identifies the assignee's project access record, so it follows
    // the assignee (same rule as the dashboard).
    const accessIdFor = (empId) =>
      members.find((m) => String(m.empId) === String(empId))?.accessId ?? null;

    const failed = [];
    let saved = 0;
    for (const t of pendingTasks) {
      const assigneeId = assigneeFor(t.assigneeId);
      try {
        await axiosInstace.post(
          "/api/emp/todos",
          {
            toDoTitle: t.title,
            remark: t.remark?.trim() || null,
            priority: t.priority || "MEDIUM",
            taskId: Number(stageId),
            stagePositionId,
            assignedEmpId: assigneeId,
            accessId: accessIdFor(assigneeId),
            listRequestDTOS: t.items.map((i) => toListRequest(i.text)),
          },
          { headers: { "Content-Type": "application/json" } },
        );
        saved += 1;
      } catch (err) {
        failed.push({
          task: t,
          error: err?.response?.data?.message || err?.message || "Failed",
        });
      }
    }

    // Keep only failed tasks (with their items) so they can be retried.
    setTasks(failed.map(({ task }) => ({ ...task, id: nextId() })));
    setItemDrafts({});
    setTaskDraft("");
    afterSave(saved, failed.length, failed[0]?.error, "task");
  };

  const saveChecklist = async () => {
    const failed = [];
    let saved = 0;
    for (const item of pendingChecklist) {
      try {
        await axiosInstace.post(
          "/api/emp/todolists/create",
          toChecklistCreateBody(todoId, item.text),
        );
        saved += 1;
      } catch (err) {
        failed.push({
          text: item.text,
          error: err?.response?.data?.message || err?.message || "Failed",
        });
      }
    }
    setQueue(failed.map(({ text }) => ({ id: nextId(), text })));
    setQueueDraft("");
    afterSave(saved, failed.length, failed[0]?.error, "checklist item");
  };

  const handleSave = async () => {
    if (!ready || saveCount === 0 || saving || hasDuplicate) return;
    setSaving(true);
    setMessage(null);
    try {
      if (tab === "task") await saveTasks();
      else await saveChecklist();
    } finally {
      setSaving(false);
      setTimeout(
        () => (tab === "task" ? taskInputRef : queueInputRef).current?.focus(),
        0,
      );
    }
  };

  /* ---------------------------------------------------------------- */

  return createPortal(
    <div
      className="quickadd-classname__overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        className="quickadd-classname__modal"
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ===== HEADER ===== */}
        <div className="quickadd-classname__header">
          <div className="quickadd-classname__header-left">
            <span
              className="quickadd-classname__header-icon"
              aria-hidden="true"
            >
              ＋
            </span>
            <h4 className="quickadd-classname__title">Quick Add</h4>
          </div>
          <button
            className="quickadd-classname__close"
            aria-label="Close"
            onClick={() => !saving && onClose()}
          >
            ✕
          </button>
        </div>

        {/* ===== TABS ===== */}
        <div className="quickadd-classname__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "task"}
            className={`quickadd-classname__tab ${tab === "task" ? "active" : ""}`}
            onClick={() => switchTab("task")}
          >
            Task <span>+ checklist</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "checklist"}
            className={`quickadd-classname__tab ${tab === "checklist" ? "active" : ""}`}
            onClick={() => switchTab("checklist")}
          >
            Checklist only
          </button>
        </div>

        {/* ===== BODY ===== */}
        <div className="quickadd-classname__body">
          <div className="quickadd-classname__field">
            <div className="quickadd-classname__field-label">Project</div>
            <div className="quickadd-classname__pills">
              {(projects || []).length === 0 && (
                <small className="quickadd-classname__hint">
                  No projects yet.
                </small>
              )}
              {(projects || []).map((p) => {
                const id = projectIdOf(p);
                return (
                  <Pill
                    key={id}
                    active={String(id) === String(projectId)}
                    title={projectNameOf(p)}
                    onClick={() => setProjectId(id)}
                  >
                    {projectNameOf(p)}
                  </Pill>
                );
              })}
            </div>
          </div>

          {projectId && (
            <div className="quickadd-classname__field">
              <div className="quickadd-classname__field-label">Stage</div>
              <div className="quickadd-classname__pills">
                {loadingProject && (
                  <small className="quickadd-classname__hint">Loading…</small>
                )}
                {!loadingProject && projectError && (
                  <small className="quickadd-classname__hint is-error">
                    {projectError}
                  </small>
                )}
                {!loadingProject && !projectError && stages.length === 0 && (
                  <small className="quickadd-classname__hint">
                    This project has no stages yet. Create a stage first.
                  </small>
                )}
                {stages.map((s) => {
                  const id = stageIdOf(s);
                  return (
                    <Pill
                      key={id}
                      active={String(id) === String(stageId)}
                      title={stageNameOf(s)}
                      onClick={() => {
                        setStageId(id);
                        setTodoId(null);
                      }}
                    >
                      {stageNameOf(s)}
                    </Pill>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "checklist" && stageId && (
            <div className="quickadd-classname__field">
              <div className="quickadd-classname__field-label">Task</div>
              <div className="quickadd-classname__pills">
                {todos.length === 0 && (
                  <small className="quickadd-classname__hint">
                    This stage has no tasks yet. Use the Task tab to add one.
                  </small>
                )}
                {todos.map((t) => {
                  const id = todoIdOf(t);
                  return (
                    <Pill
                      key={id}
                      active={String(id) === String(todoId)}
                      title={todoNameOf(t)}
                      onClick={() => setTodoId(id)}
                    >
                      {todoNameOf(t)}
                    </Pill>
                  );
                })}
              </div>
            </div>
          )}

          {/* ---------- Task tab entry ---------- */}
          {tab === "task" && (
            <div className="quickadd-classname__field">
              <label
                className="quickadd-classname__field-label"
                htmlFor="quickadd-task-input"
              >
                Tasks
              </label>

              {/* Defaults for the next task(s) you add */}
              <div className="quickadd-classname__defaults">
                <div className="quickadd-classname__meta-row">
                  <span className="quickadd-classname__meta-label">
                    Priority
                  </span>
                  <div className="quickadd-classname__pills quickadd-classname__pills--sm">
                    {PRIORITIES.map((p) => (
                      <Pill
                        key={p}
                        active={defaultPriority === p}
                        tone={p.toLowerCase()}
                        onClick={() => setDefaultPriority(p)}
                      >
                        {priorityLabel(p)}
                      </Pill>
                    ))}
                  </div>
                </div>
                {canAssign && members.length > 0 && (
                  <div className="quickadd-classname__meta-row">
                    <span className="quickadd-classname__meta-label">
                      Assign to
                    </span>
                    <div className="quickadd-classname__pills quickadd-classname__pills--sm">
                      {members.map((m) => (
                        <Pill
                          key={m.empId}
                          active={String(defaultAssignee) === String(m.empId)}
                          avatar={m.empName}
                          title={m.empName}
                          onClick={() => setDefaultAssignee(m.empId)}
                        >
                          {m.empName}
                          {String(m.empId) === String(currentEmpId)
                            ? " (You)"
                            : ""}
                        </Pill>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <input
                id="quickadd-task-input"
                ref={taskInputRef}
                className="quickadd-classname__input"
                placeholder={
                  ready
                    ? "Task name, then press Enter"
                    : "Pick a project and stage first"
                }
                value={taskDraft}
                disabled={!ready || saving}
                onChange={(e) => setTaskDraft(e.target.value)}
                onKeyDown={onTaskKeyDown}
                onPaste={onTaskPaste}
              />
              {taskDraft.trim() && isDuplicate(taskDraft) && (
                <small className="quickadd-classname__hint is-error">
                  This task name already exists in this stage. Change the name.
                </small>
              )}
              <small className="quickadd-classname__hint">
                Press Enter to add each task. To add checklist items, click the
                "+ Checklist item" box on that task. Press Esc there to go back
                to the task box.
              </small>

              {tasks.length > 0 && (
                <ul className="quickadd-classname__tasks">
                  {tasks.map((t) => (
                    <li
                      key={t.id}
                      className={`quickadd-classname__task-card quickadd-classname__task-card--${String(
                        t.priority || "MEDIUM",
                      ).toLowerCase()}`}
                    >
                      <div className="quickadd-classname__task-head">
                        {editingTaskId === t.id ? (
                          <input
                            className="quickadd-classname__input quickadd-classname__input--sub"
                            defaultValue={t.title}
                            autoFocus
                            disabled={saving}
                            onFocus={(e) => e.target.select()}
                            onBlur={(e) =>
                              commitTaskTitle(t.id, e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                commitTaskTitle(t.id, e.currentTarget.value);
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                e.stopPropagation(); // don't close the popup
                                setEditingTaskId(null);
                              }
                            }}
                          />
                        ) : (
                          <>
                            <span
                              className="quickadd-classname__task-title"
                              title={t.title}
                            >
                              {t.title}
                            </span>
                            <button
                              type="button"
                              className="quickadd-classname__icon-btn"
                              aria-label={`Edit task name ${t.title}`}
                              title="Edit task name"
                              disabled={saving}
                              onClick={() => setEditingTaskId(t.id)}
                            >
                              <FiEdit2 />
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className="quickadd-classname__icon-btn"
                          aria-label={`Remove task ${t.title}`}
                          disabled={saving}
                          onClick={() =>
                            setTasks((all) => all.filter((x) => x.id !== t.id))
                          }
                        >
                          <FiX />
                        </button>
                      </div>

                      {isDuplicate(t.title) && (
                        <small className="quickadd-classname__hint is-error">
                          This task name already exists in this stage. Click the
                          pencil to rename it.
                        </small>
                      )}

                      <div className="quickadd-classname__meta-row">
                        <span className="quickadd-classname__meta-label">
                          Priority
                        </span>
                        <div className="quickadd-classname__pills quickadd-classname__pills--sm">
                          {PRIORITIES.map((p) => (
                            <Pill
                              key={p}
                              active={t.priority === p}
                              tone={p.toLowerCase()}
                              disabled={saving}
                              onClick={() => patchTask(t.id, { priority: p })}
                            >
                              {priorityLabel(p)}
                            </Pill>
                          ))}
                        </div>
                      </div>
                      {canAssign && members.length > 0 && (
                        <div className="quickadd-classname__meta-row">
                          <span className="quickadd-classname__meta-label">
                            Assign to
                          </span>
                          <div className="quickadd-classname__pills quickadd-classname__pills--sm">
                            {members.map((m) => (
                              <Pill
                                key={m.empId}
                                active={
                                  String(t.assigneeId) === String(m.empId)
                                }
                                avatar={m.empName}
                                title={m.empName}
                                disabled={saving}
                                onClick={() =>
                                  patchTask(t.id, { assigneeId: m.empId })
                                }
                              >
                                {m.empName}
                                {String(m.empId) === String(currentEmpId)
                                  ? " (You)"
                                  : ""}
                              </Pill>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="quickadd-classname__meta-row">
                        <span className="quickadd-classname__meta-label">
                          Remark
                        </span>
                        {t.remark === null ? (
                          <button
                            type="button"
                            className="quickadd-classname__pill"
                            disabled={saving}
                            onClick={() => patchTask(t.id, { remark: "" })}
                          >
                            <FiPlus aria-hidden="true" /> Add remark
                          </button>
                        ) : (
                          <span className="quickadd-classname__item">
                            <input
                              className="quickadd-classname__input quickadd-classname__input--sub"
                              placeholder="Type a remark"
                              value={t.remark}
                              disabled={saving}
                              autoFocus
                              onChange={(e) =>
                                patchTask(t.id, { remark: e.target.value })
                              }
                            />
                            <button
                              type="button"
                              aria-label={`Delete remark for ${t.title}`}
                              title="Delete remark"
                              disabled={saving}
                              onClick={() => patchTask(t.id, { remark: null })}
                            >
                              <FiX />
                            </button>
                          </span>
                        )}
                      </div>

                      {t.items.length > 0 && (
                        <div className="quickadd-classname__items">
                          {t.items.map((i) => (
                            <span
                              key={i.id}
                              className="quickadd-classname__item"
                            >
                              <span
                                className="quickadd-classname__item-text"
                                title={i.text}
                              >
                                {i.text}
                              </span>
                              <button
                                type="button"
                                aria-label={`Remove ${i.text}`}
                                disabled={saving}
                                onClick={() => removeItem(t.id, i.id)}
                              >
                                <FiX />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      <input
                        ref={(el) => {
                          if (el) itemInputRefs.current[t.id] = el;
                          else delete itemInputRefs.current[t.id];
                        }}
                        className="quickadd-classname__input quickadd-classname__input--sub"
                        placeholder="+ Checklist item, press Enter"
                        value={itemDrafts[t.id] || ""}
                        disabled={saving}
                        onChange={(e) =>
                          setItemDrafts((d) => ({
                            ...d,
                            [t.id]: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => onItemKeyDown(e, t.id)}
                        onPaste={(e) => onItemPaste(e, t.id)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* ---------- Checklist tab entry ---------- */}
          {tab === "checklist" && (
            <div className="quickadd-classname__field">
              <label
                className="quickadd-classname__field-label"
                htmlFor="quickadd-checklist-input"
              >
                Checklist items
              </label>

              {queue.length > 0 && (
                <div className="quickadd-classname__items">
                  {queue.map((i) => (
                    <span key={i.id} className="quickadd-classname__item">
                      <span
                        className="quickadd-classname__item-text"
                        title={i.text}
                      >
                        {i.text}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${i.text}`}
                        disabled={saving}
                        onClick={() =>
                          setQueue((q) => q.filter((x) => x.id !== i.id))
                        }
                      >
                        <FiX />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <input
                id="quickadd-checklist-input"
                ref={queueInputRef}
                className="quickadd-classname__input"
                placeholder={
                  ready
                    ? "Checklist item, then press Enter"
                    : "Pick a project, stage and task first"
                }
                value={queueDraft}
                disabled={!ready || saving}
                onChange={(e) => setQueueDraft(e.target.value)}
                onKeyDown={onQueueKeyDown}
                onPaste={onQueuePaste}
              />
              <small className="quickadd-classname__hint">
                Enter adds to the list. Paste several lines to add them all at
                once.
              </small>
            </div>
          )}
        </div>

        {/* ===== FOOTER ===== */}
        <div className="quickadd-classname__footer">
          <button
            className="quickadd-classname__btn quickadd-classname__btn--cancel"
            onClick={() => !saving && onClose()}
            disabled={saving}
          >
            Close
          </button>
          <button
            className="quickadd-classname__btn quickadd-classname__btn--save"
            onClick={handleSave}
            disabled={!ready || saveCount === 0 || saving || hasDuplicate}
            title={
              hasDuplicate
                ? "Rename the duplicate task name(s) first"
                : undefined
            }
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/* Sidebar button                                                      */
/* ------------------------------------------------------------------ */

export default function SidebarQuickAdd({
  projects,
  activeProjectId,
  collapsed,
  onOpen,
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="quickadd-classname__trigger-wrap">
        <button
          type="button"
          className="quickadd-classname__trigger"
          title="Quick Add"
          onClick={() => {
            setOpen(true);
            onOpen?.();
          }}
        >
          <FiPlus aria-hidden="true" />
          {!collapsed && <span>Quick Add</span>}
        </button>
      </div>

      {open && (
        <QuickAddModal
          projects={projects}
          defaultProjectId={activeProjectId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
